import { defineDecimalPlaces } from '../round-functions/get-decimal-places';
import { eNumber } from '../round-functions/e-number';
import { mathCeilDecimal } from '../round-functions/math-ceil-decimal';
import store from '../../state/store';

const MAX_INTEGER_DIGITS = 20;

const addCommas = (nStr, thousandSeparator = ',', decimalSeparator = '.') => {
  nStr += '';
  let x = nStr.split('.'),
    x1 = x[0],
    x2 = x.length > 1 ? decimalSeparator + x[1] : '',
    rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1' + thousandSeparator + '$2');
  }
  return x1 + x2;
};

export const numberFormat = (
  value,
  currency,
  withDecimals = false,
  withThousandSeparator = false,
  isFee = false,
  isParsingInput = false,
  useAmountFirstRules = false,
) => {
  const state = store.getState();
  const layout = state?.initTheme?.theme_editor_settings?.customization_settings?.layout;
  const isAmountFirstLayout = layout === 'amount-first' || useAmountFirstRules;
  const language = state?.profileInfo?.customer?.language || navigator.language || 'en-US';

  const isNumber = typeof value === 'number';
  let decimalPlaces = defineDecimalPlaces(currency);
  value = eNumber(value);

  let thousandSeparator = ',';
  let decimalSeparator = '.';
  if (isAmountFirstLayout) {
    const lang = (language || '').toLowerCase();
    const useNeutralNumberLocale = lang.startsWith('ar') || lang === 'he';
    const numberLocale = useNeutralNumberLocale ? 'en-US' : language;

    try {
      const parts = new Intl.NumberFormat(numberLocale).formatToParts(1234567.89);
      decimalSeparator = parts.find((p) => p.type === 'decimal')?.value || '.';
      thousandSeparator = parts.find((p) => p.type === 'group')?.value;

      if (!thousandSeparator || thousandSeparator === decimalSeparator) {
        thousandSeparator = decimalSeparator === ',' ? '.' : ',';
      }
    } catch (error) {}
  }

  let processedValue = value.toString();
  if (isAmountFirstLayout && !isNumber && isParsingInput) {
    const otherSeparator = decimalSeparator === '.' ? ',' : '.';
    const endsWithDecimal =
      processedValue.endsWith(decimalSeparator) || processedValue.endsWith(otherSeparator);

    if (thousandSeparator) {
      processedValue = processedValue.split(thousandSeparator).join('');
    }

    processedValue = processedValue
      .split(decimalSeparator)
      .join('.')
      .split(otherSeparator)
      .join('.');

    if (endsWithDecimal && !processedValue.endsWith('.')) {
      processedValue += '.';
    }
  }

  value = processedValue
    .replace(/[^\d.]*/g, '')
    .replace(/(\.)\1+/g, '$1')
    .replace(new RegExp(`^[^\\d]*(\\d{1,${MAX_INTEGER_DIGITS}}([.]\\d{0,10})?).*$`, 'g'), '$1');

  if ((withDecimals || withThousandSeparator) && value.startsWith('.')) {
    value = '0' + value;
  }

  if (value.indexOf('.') != '-1') {
    if (isAmountFirstLayout && isFee) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const roundedValue = mathCeilDecimal(numValue, currency);
        value = roundedValue.toFixed(decimalPlaces);
      } else {
        value = value.substring(0, value.indexOf('.') + (+decimalPlaces + 1));
      }
    } else {
      value = value.substring(0, value.indexOf('.') + (+decimalPlaces + 1));
    }
  }
  if (withDecimals && value.indexOf('.') == '-1') {
    value = value + '.';
    for (let i = 0; i < decimalPlaces; i++) {
      value += '0';
    }
  }
  if (withDecimals && value.indexOf('.') != '-1' && isAmountFirstLayout) {
    const parts = value.split('.');
    const integerPart = parts[0];
    let decimalPart = parts[1] || '';
    while (decimalPart.length < decimalPlaces) {
      decimalPart += '0';
    }
    value = integerPart + '.' + decimalPart;
  }
  if (value.indexOf('.') != '-1' && decimalPlaces === 0) {
    value = value.slice(0, value.indexOf('.'));
  }

  if (withThousandSeparator) {
    value = addCommas(value, thousandSeparator, decimalSeparator);
  }
  return value;
};
